import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useLanguage } from '@/context/LanguageContext'
import { useTheme } from '@/context/ThemeContext'
import { BRAND_GUIDE_URL } from '@/utils/constants'
import useIntl from '@/utils/intl'
import { Alert, Button, Snackbar } from '@mui/material'
import Image from 'next/image'
import React, { FunctionComponent, useRef, useState } from 'react'

const LogoPage: FunctionComponent = () => {
  const { locale } = useLanguage()
  const { theme } = useTheme()
  const { translate } = useIntl()
  const logoRef = useRef<HTMLDivElement>(null)
  const spaceRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const warningRef = useRef<HTMLDivElement>(null)
  const dontRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLDivElement>(null)
  const colorRef = useRef<HTMLDivElement>(null)
  // why
  const [selectedService, setSelectedService] = useState<number | undefined>()
  const [copied, setCopied] = useState(false)
  const rgb = BRAND_GUIDE_URL + '/Logo.zip'
  const brandguide = BRAND_GUIDE_URL + '/BrandGuide.zip'
  const LogoSpace = BRAND_GUIDE_URL + '/3.png'
  const LogoElementWhite = BRAND_GUIDE_URL + '/1.png'
  const LogoElementDark = BRAND_GUIDE_URL + '/2.png'
  const LogoBackground = BRAND_GUIDE_URL + '/logoBackground.png'
  const LogoBackgroundComponent = BRAND_GUIDE_URL + '/8.png'
  const LogoBackgroundComponentEng = BRAND_GUIDE_URL + '/8eng.png'
  const LogoTextMn = BRAND_GUIDE_URL + '/4.png'
  const LogoTextEn = BRAND_GUIDE_URL + '/5.png'
  const WrongImage1 = BRAND_GUIDE_URL + '/9.png'
  const WrongImage2 = BRAND_GUIDE_URL + '/10.png'
  const WrongImage3 = BRAND_GUIDE_URL + '/11.png'
  const WrongImage4 = BRAND_GUIDE_URL + '/12.png'
  const WrongImage5 = BRAND_GUIDE_URL + '/13.png'
  const WrongImage6 = BRAND_GUIDE_URL + '/14.png'
  const WrongImage7 = BRAND_GUIDE_URL + '/15.png'
  const WrongImage8 = BRAND_GUIDE_URL + '/16.png'
  const WrongImage9 = BRAND_GUIDE_URL + '/17.png'

  // copy
  const handleCopyRed = (): void => {
    const textToCopy = '#FF0014'
    void navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // copy
  const handleCopyBlack = (): void => {
    const textToCopy = '#000000'
    void navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // download file
  const onDownload = (): void => {
    const link = document.createElement('a')
    link.download = 'RGB.zip'
    link.href = `${rgb}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const onDownloadGuide = (): void => {
    const link = document.createElement('a')
    link.download = 'brandguide.zip'
    link.href = `${brandguide}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // sideMenu
  const handleTarget = (sectionId: number): void => {
    setSelectedService(sectionId)
    switch (sectionId) {
      case 1:
        warningRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      case 2:
        logoRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      case 3:
        spaceRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      case 4:
        colorRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      case 5:
        backRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      case 6:
        dontRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      case 7:
        textRef.current?.scrollIntoView({ behavior: 'smooth' })
        break
      default:
        break
    }
  }

  const data = [
    {
      id: 1,
      nameMn: 'Анхаарах зүйлс',
      nameEn: 'Logo usage advice'
    },
    {
      id: 2,
      nameMn: 'Логоны элемент',
      nameEn: 'Logo element'
    },
    {
      id: 3,
      nameMn: 'Логоны зай',
      nameEn: 'Logo clear space'
    },
    {
      id: 4,
      nameMn: 'Логоны өнгө',
      nameEn: 'Color palette'
    },
    {
      id: 5,
      nameMn: 'Логоны арын дэвсгэр',
      nameEn: 'Logo background'
    },
    {
      id: 6,
      nameMn: 'Логоны зохисгүй хэрэглээ',
      nameEn: 'Helpful reminders for using the logo'
    },
    {
      id: 7,
      nameMn: 'Текст',
      nameEn: 'Text notation'
    }
  ]

  // Styles
  const ContainerStyles = (theme === 'dark' ? 'bg-darkBg flex flex-col rounded-xl p-8 my-20' : 'flex flex-col bg-mainbg rounded-xl p-8 my-20')
  const SubContainerStyles = 'flex flex-col justify-center items-start'
  const TitleStyles = (theme === 'dark' ? 'font-bold md:text-4xl text-xl text-textHigh' : 'font-bold md:text-4xl text-xl text-textHigh')
  const DescriptionStyles = 'text-textMid text-sm font-medium indent-3'
  const DescriptionStylesNo = 'text-textMid text-sm font-medium my-5 '
  const ImageContainerStyles = 'flex flex-row justify-between items-start my-5 xs:flex-col xl:flex-row'
  const ImageStyles = 'xl:w-[49%] bg-white h-80 rounded-xl xs:w-full my-5 flex justify-center items-center'
  const ImageStylesDark = 'xl:w-[49%] bg-darkBg border-2 h-80 rounded-xl xs:w-full my-5 flex justify-center items-center'
  const ButtonStyles = (theme === 'dark' ? 'bg-gradient-to-r from-[#F7F7F7] to-[#F7F7F7] text-textInverse rounded-xl py-3 justify-center normal-case xl:w-52 xs:w-full' : 'bg-gradient-to-r from-[#000000] to-[#000000] text-textInverse rounded-xl py-3 justify-center normal-case xl:w-52 xs:w-full')

  return (
    <div className='w-full flex justify-center'>
      <div className='bg-surfaceInverse w-[95vw] rounded-2xl lg:p-14 md:p-8 p-6 flex md:flex-row justify-between flex-col-reverse'>
        <div className='flex flex-col mt-8 md:mt-0 xl:w-2/3 md:w-3/5'>
          <div className='rounded-xl bg-cover bg-center w-full md:aspect-[2/1]' style={{ backgroundImage: `url(${LogoBackground})` }}>
            <div className='h-full w-full bg-gradient-to-t from-black/80 via-black/60 to-black/80 xl:from-black/60 rounded-xl p-14 flex flex-col justify-end'>
              <div className='flex flex-col justify-center items-center md:justify-start md:items-start'>
                <span className='text-textAdditional xl:text-4xl text-xl font-extrabold'>{translate('logoTitle')}</span>
                <span className='text-textAdditional text-sm md:text-base font-medium mt-4'>{translate('logoDesc')}</span>
              </div>
            </div>
          </div>
          <div>
            <div ref={warningRef} className={ContainerStyles}>
              <div className={SubContainerStyles}>
                <a href='#logo' className={TitleStyles}>{translate('logowarningTitle')}</a>
                <p className={DescriptionStylesNo}>{translate('logoWarning')}</p>
                <p className={DescriptionStyles}>• {translate('logowarningDesc1')}</p>
                <p className={DescriptionStyles}>• {translate('logowarningDesc2')}</p>
                <p className={DescriptionStyles}>• {translate('logowarningDesc3')}</p>
                <p className={DescriptionStyles}>• {translate('logowarningDesc4')}</p>
                <p className={DescriptionStylesNo}>{translate('logowarningDesc5')}</p>
                <Button onClick={onDownloadGuide} className={ButtonStyles}>
                  <p>{translate('guideDownload')}</p>
                </Button>
              </div>
            </div>
            <div ref={logoRef} className={ContainerStyles}>
              <div className={SubContainerStyles}>
                <a href='#logo' className={TitleStyles}>{translate('logoElement')}</a>
                <p className={DescriptionStylesNo}>{translate('logoElementDesc')}</p>
                <Button onClick={onDownload} className={ButtonStyles}>
                  <p>{translate('logoDownload')}</p>
                </Button>
              </div>
              <div className={ImageContainerStyles}>
                <div className={ImageStyles}>
                  <Image alt='logoWhite' width={250} height={250} src={LogoElementWhite} />
                </div>
                <div className={ImageStylesDark}>
                  <Image alt='logoDark' width={250} height={250} src={LogoElementDark} />
                </div>
              </div>
            </div>
            <div ref={spaceRef} className={ContainerStyles}>
              <div className={SubContainerStyles}>
                <a href='#space' className={TitleStyles}>{translate('logoSpace')}</a>
                <p className={DescriptionStylesNo}>{translate('logoSpaceDesc')}</p>
              </div>
              <div className={ImageContainerStyles}>
                <Image alt='logoSpace' className='w-full bg-white rounded-xl p-5' width={1000} height={1000} src={LogoSpace} />
              </div>
            </div>
            <div ref={colorRef} className={ContainerStyles}>
              <div className={SubContainerStyles}>
                <a href='#color' className={TitleStyles}>{translate('logoColor')}</a>
                <p className={DescriptionStylesNo}>{translate('logoColorDesc')}</p>
              </div>
              <div className={ImageContainerStyles}>
                <div className='flex justify-center items-center space-x-5 xl:w-[49%] bg-white h-80 rounded-xl xs:w-full my-5 bg-cover bg-center px-5'>
                  <div onClick={handleCopyRed} className='bg-mainRed hover:opacity-50 transition-all duration-300 rounded-xl w-[100px] h-[100px]' />
                  <div>
                    <h1 className={(theme === 'dark' ? 'font-bold md:text-4xl text-xl text-black' : 'font-bold md:text-4xl text-xl text-textHigh')}>Pantone 185C</h1>
                    <p className={(theme === 'dark' ? 'text-black text-sm font-medium indent-3' : 'text-textMid text-sm font-medium indent-3')}>CMYK C0 M100 Y90 K0</p>
                    <p className={(theme === 'dark' ? 'text-black text-sm font-medium indent-3' : 'text-textMid text-sm font-medium indent-3')}>RGB R255 G0 B20</p>
                  </div>
                </div>
                <div className='flex justify-center items-center space-x-5 xl:w-[49%] bg-white h-80 rounded-xl xs:w-full my-5 bg-cover bg-center px-5'>
                  <div onClick={handleCopyBlack} className='bg-mainBlack hover:opacity-50 transition-all duration-300 rounded-xl w-[100px] h-[100px]' />
                  <div>
                    <h1 className={(theme === 'dark' ? 'font-bold md:text-4xl text-xl text-black' : 'font-bold md:text-4xl text-xl text-textHigh')}>Pantone Black</h1>
                    <p className={(theme === 'dark' ? 'text-black text-sm font-medium indent-3' : 'text-textMid text-sm font-medium indent-3')}>CMYK C0 M0 Y0 K100</p>
                    <p className={(theme === 'dark' ? 'text-black text-sm font-medium indent-3' : 'text-textMid text-sm font-medium indent-3')}>RGB R0 G0 B0</p>
                  </div>
                </div>
              </div>
            </div>
            <div ref={backRef} className={ContainerStyles}>
              <div className={SubContainerStyles}>
                <a href='#color' className={TitleStyles}>{translate('logoBack')}</a>
              </div>
              {locale === 'mn' ? <Image alt='background' className='w-full border-2 rounded-xl my-10' width={1000} height={1000} src={LogoBackgroundComponent} /> : <Image alt='background' className='w-full border-2 rounded-xl my-10' width={1000} height={1000} src={LogoBackgroundComponentEng} />}
            </div>
            <div ref={dontRef} className={ContainerStyles}>
              <div className={SubContainerStyles}>
                <a href='#color' className={TitleStyles}>{translate('logoDontUse')}</a>
              </div>
              <div className='flex flex-col justify-center items-center my-10'>
                <div className='w-full flex flex-col space-x-3 xl:flex-row justify-between items-center'>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage1} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc1')}</p>
                  </div>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage2} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc2')}</p>
                  </div>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage3} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc3')}</p>
                  </div>
                </div>
                <div className='w-full flex flex-col space-x-3 xl:flex-row justify-between items-center'>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage4} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc4')}</p>
                  </div>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage5} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc5')}</p>
                  </div>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage6} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc6')}</p>
                  </div>
                </div>
                <div className='w-full flex flex-col space-x-3 xl:flex-row justify-between items-center'>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage7} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc7')}</p>
                  </div>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage8} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc8')}</p>
                  </div>
                  <div className='text-center justify-center items-center flex flex-col'>
                    <Image alt='dont use' width={330} height={330} className='rounded-xl' src={WrongImage9} />
                    <p className={`${DescriptionStylesNo} w-60 h-20`}>{translate('logoDontuseDesc9')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div ref={textRef} className={ContainerStyles}>
              <div className={SubContainerStyles}>
                <a href='#text' className={TitleStyles}>{translate('logoText')}</a>
                <p className={DescriptionStyles}>{translate('logoTextDesc')}</p>
              </div>
              <div className={ImageContainerStyles}>
                <div className={ImageStyles}>
                  <Image alt='text' width={250} height={250} src={LogoTextMn} />
                </div>
                <div className={ImageStyles}>
                  <Image alt='text' width={250} height={250} src={LogoTextEn} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Snackbar
          open={copied}
          autoHideDuration={2000}
          onClose={() => setCopied(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setCopied(false)} severity='success'>
            {translate('copy')}
          </Alert>
        </Snackbar>
        <div className='md:border-l border-secondaryMidStroke md:pl-12 md:ml-12 xl:w-1/3 md:w-2/5'>
          <div className='sticky top-36'>
            <div className='flex flex-col gap-3 mt-14'>
              {data.map((item) => (
                <DarkButton
                  key={item.id}
                  fullwidth
                  type={selectedService === item.id ? undefined : 'outlined'}
                  size='sm'
                  onClick={() => handleTarget(item.id)}
                >
                  {locale === 'mn' ? item.nameMn : item.nameEn}
                </DarkButton>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default LogoPage
